import { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Header } from './components/Header';
import { LanguageSelector } from './components/LanguageSelector';
import VersionLabel from './components/VersionLabel';

export const MainLayout = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsReady(true);
    }, 300);
  }, []);

  useEffect(() => {
    // List of paths where panning should be allowed
    const allowPanningPaths = [
      '/intro',
      '/splash',
      '/outro',
      '/thanks',
      '/game_quizz',
    ];
    const shouldAllowPanning = allowPanningPaths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}`)
    );

    // If we're on a page where panning is allowed, don't apply restrictions
    if (shouldAllowPanning) {
      return;
    }

    // Apply CSS to prevent overscroll behavior (bouncing/panning)
    const originalOverscrollBehavior =
      document.documentElement.style.overscrollBehavior;
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.overflow = 'hidden';

    // Prevent pinch zoom while allowing scrolling on text content
    const handleTouchMove = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();
      const isScrollableElement =
        ['input', 'textarea', 'select'].includes(tagName) ||
        target.classList.contains('scrollable') ||
        target.classList.contains('text-scrollable');

      // Prevent pinch zoom (multi-touch gestures)
      if (event.touches.length > 1) {
        event.preventDefault();
        return;
      }

      // Allow scrolling on scrollable elements
      if (!isScrollableElement) {
        event.preventDefault();
      }
    };

    // Add event listeners with passive: false to allow preventDefault
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    // Cleanup function
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      // Reset CSS
      document.documentElement.style.overscrollBehavior =
        originalOverscrollBehavior;
      document.body.style.overscrollBehavior = '';
      document.body.style.overflow = '';
    };
  }, [location.pathname]);

  if (!isReady) {
    return <div className="flex h-screen items-center justify-center"></div>;
  }

  return (
    <div className="mainBackground flex h-screen w-full justify-center">
      <div className="flex h-screen w-full flex-col md:max-w-3xl">
        <Header />
        <LanguageSelector />
        <div id="content" className="flex grow">
          {children}
        </div>
      </div>
      <VersionLabel />
    </div>
  );
};
